<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDealsStore } from 'dashboard/stores/deals';
import { useAlert } from 'dashboard/composables';
import { useCommercialFeatures } from 'dashboard/composables/useCommercialFeatures';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import CommercialQuotesPanel from '../components/CommercialQuotesPanel.vue';

const route = useRoute();
const router = useRouter();
const store = useDealsStore();
const { t } = useI18n();
const { hasFeature: hasCommercialFeature } = useCommercialFeatures();
const deal = ref(null);
const activeTab = ref('timeline');
const isSaving = ref(false);
const isEditing = ref(false);
const lossOpen = ref(false);
const wonOpen = ref(false);
const lossReasonId = ref('');
const selectedFile = ref(null);
const activityModalOpen = ref(false);
const editingActivityId = ref(null);
const activityMetadata = ref({});
const cancelActivityCandidate = ref(null);
const detailsModalOpen = ref(false);
const contactsModalOpen = ref(false);
const companyModalOpen = ref(false);

const activityForm = reactive({
  activityType: 'follow_up',
  status: 'completed',
  occurredAt: new Date().toISOString().slice(0, 16),
  scheduledAt: '',
  completedAt: '',
  notes: '',
});
const activityModalForm = reactive({
  activityType: 'follow_up',
  subject: '',
  date: '',
  time: '',
  durationMinutes: 30,
  description: '',
  status: 'pending',
  assignedUserId: '',
  location: '',
  companyId: '',
  companyName: '',
  contactId: '',
  contactName: '',
  invitedUserIds: [],
  externalInvitees: '',
});
const commentBody = ref('');
const documentForm = reactive({ name: '', category: 'document', metadata: {} });
const editForm = reactive({});

const tabs = computed(() => [
  ['timeline', t('COMMERCIAL.DETAIL.TABS.TIMELINE')],
  ['activities', t('COMMERCIAL.DETAIL.TABS.ACTIVITIES')],
  ['documents', t('COMMERCIAL.DETAIL.TABS.DOCUMENTS')],
  ...(hasCommercialFeature(FEATURE_FLAGS.BOTZ_CRM_QUOTES)
    ? [['quotations', t('COMMERCIAL.DETAIL.TABS.QUOTATIONS')]]
    : []),
  ['products', t('COMMERCIAL.DETAIL.TABS.PRODUCTS')],
  ['orders', t('COMMERCIAL.DETAIL.TABS.ORDERS')],
  ['emails', t('COMMERCIAL.DETAIL.TABS.EMAILS')],
  ['companies', t('COMMERCIAL.DETAIL.TABS.COMPANIES')],
  ['comments', t('COMMERCIAL.DETAIL.TABS.COMMENTS')],
  ['updates', t('COMMERCIAL.DETAIL.TABS.UPDATES')],
]);
const activityTypes = computed(() => [
  ['call', t('COMMERCIAL.ACTIVITY_TYPES.CALL')],
  ['email', t('COMMERCIAL.ACTIVITY_TYPES.EMAIL')],
  ['whatsapp', t('COMMERCIAL.ACTIVITY_TYPES.WHATSAPP')],
  ['meeting', t('COMMERCIAL.ACTIVITY_TYPES.MEETING')],
  ['video_call', t('COMMERCIAL.ACTIVITY_TYPES.VIDEO_CALL')],
  ['visit', t('COMMERCIAL.ACTIVITY_TYPES.VISIT')],
  ['quotation_sent', t('COMMERCIAL.ACTIVITY_TYPES.QUOTATION_SENT')],
  ['follow_up', t('COMMERCIAL.ACTIVITY_TYPES.FOLLOW_UP')],
  ['internal_task', t('COMMERCIAL.ACTIVITY_TYPES.INTERNAL_TASK')],
]);
const activityLabels = computed(() => Object.fromEntries(activityTypes.value));
const activityStatusLabels = computed(() => ({
  pending: t('COMMERCIAL.ACTIVITY_STATUS.PENDING'),
  completed: t('COMMERCIAL.ACTIVITY_STATUS.COMPLETED'),
  cancelled: t('COMMERCIAL.ACTIVITY_STATUS.CANCELLED'),
}));
const quoteStatusLabels = computed(() => ({
  draft: t('COMMERCIAL.QUOTES.STATUS.DRAFT'),
  sent: t('COMMERCIAL.QUOTES.STATUS.SENT'),
  accepted: t('COMMERCIAL.QUOTES.STATUS.ACCEPTED'),
  rejected: t('COMMERCIAL.QUOTES.STATUS.REJECTED'),
  expired: t('COMMERCIAL.QUOTES.STATUS.EXPIRED'),
  cancelled: t('COMMERCIAL.QUOTES.STATUS.CANCELLED'),
}));
const plannedActivities = computed(() =>
  (deal.value?.activities || []).filter(item => item.status === 'pending')
);
const completedActivities = computed(() =>
  (deal.value?.activities || []).filter(item => item.status !== 'pending')
);
const documentNamePlaceholder = computed(
  () =>
    ({
      documents: t('COMMERCIAL.DETAIL.FORMS.DOCUMENTS_NAME'),
      products: t('COMMERCIAL.DETAIL.FORMS.PRODUCTS_NAME'),
      orders: t('COMMERCIAL.DETAIL.FORMS.ORDERS_NAME'),
    })[activeTab.value]
);
const recordsForTab = computed(() => {
  const category = {
    documents: 'document',
    products: 'product',
    orders: 'order',
  }[activeTab.value];
  return category
    ? deal.value.documents.filter(item => item.subtype === category)
    : [];
});
const stageDurationById = computed(() =>
  Object.fromEntries(
    (deal.value?.stageDurations || []).map(item => [item.stage.id, item.days])
  )
);
const stageLabel = stage => stage.name;
const visibleStages = computed(() =>
  store.stages.filter(stage => stage.stageType === 'open')
);
const wonStage = computed(() =>
  store.stages.find(stage => stage.stageType === 'won')
);
const lostStage = computed(() =>
  store.stages.find(stage => stage.stageType === 'lost')
);
const currentStagePosition = computed(
  () =>
    store.stages.find(stage => stage.id === deal.value?.currentStageId)
      ?.position || 0
);
const hasStageDuration = stage =>
  Object.prototype.hasOwnProperty.call(stageDurationById.value, stage.id);
const warrantyDate = computed(
  () =>
    deal.value?.company.customAttributes?.warranty_date ||
    deal.value?.company.additionalAttributes?.warranty_date
);
const companySource = computed(
  () =>
    deal.value?.company.customAttributes?.source ||
    deal.value?.company.additionalAttributes?.source
);
const contactRows = computed(() =>
  deal.value?.contact ? [deal.value.contact] : []
);
const customValue = (record, ...keys) => {
  const attributes = {
    ...(record?.additionalAttributes || {}),
    ...(record?.customAttributes || {}),
  };
  return keys.map(key => attributes[key]).find(Boolean) || '—';
};
const money = (amount, currency = 'COP') =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));
const dateTime = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';
const date = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
        new Date(value)
      )
    : '—';
const updateMeta = entry =>
  [dateTime(entry.occurredAt), entry.user?.name || t('COMMERCIAL.SYSTEM')].join(
    ' · '
  );

const applyQuickAction = () => {
  const action = route.query.action;
  if (
    action === 'quotation' &&
    !hasCommercialFeature(FEATURE_FLAGS.BOTZ_CRM_QUOTES)
  ) {
    return;
  }
  const actionConfig = {
    activity: ['activities', 'follow_up'],
    whatsapp: ['activities', 'whatsapp'],
    comment: ['comments', null],
    email: ['emails', 'email'],
    document: ['documents', null],
    quotation: ['quotations', null],
  }[action];
  if (!actionConfig) return;
  activeTab.value = actionConfig[0];
  if (actionConfig[1]) activityForm.activityType = actionConfig[1];
};

const loadDeal = async () => {
  try {
    if (!store.stages.length) await store.fetchPipeline();
    deal.value = await store.showDeal(route.params.dealId);
    Object.assign(editForm, {
      name: deal.value.name,
      amount: deal.value.amount,
      currency: deal.value.currency,
      assignedUserId: deal.value.assignedUserId || '',
      expectedCloseDate: deal.value.expectedCloseDate || '',
      source: deal.value.source || '',
      businessLine: deal.value.businessLine || '',
      probability: deal.value.probability,
      description: deal.value.description || '',
      contactId: deal.value.contactId || '',
      companyId: deal.value.companyId,
    });
    applyQuickAction();
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.LOAD_ERROR'));
  }
};

const refresh = async () => {
  deal.value = await store.refreshDeal(deal.value.id);
};

const saveDeal = async () => {
  isSaving.value = true;
  try {
    deal.value = await store.updateDeal(deal.value.id, editForm);
    isEditing.value = false;
    useAlert(t('COMMERCIAL.MESSAGES.UPDATED'));
  } finally {
    isSaving.value = false;
  }
};

const moveToStage = async stage => {
  if (stage.id === deal.value.currentStageId) return;
  isSaving.value = true;
  try {
    deal.value = await store.moveDeal(deal.value.id, stage.id);
    await refresh();
  } finally {
    isSaving.value = false;
  }
};

const markLost = async () => {
  if (lostStage.value?.requiresLossReason && !lossReasonId.value) {
    useAlert(t('COMMERCIAL.MESSAGES.LOSS_REASON_REQUIRED'));
    return;
  }
  deal.value = await store.moveDeal(
    deal.value.id,
    lostStage.value.id,
    lossReasonId.value || null
  );
  lossOpen.value = false;
  await refresh();
};

const markWon = async () => {
  isSaving.value = true;
  try {
    deal.value = await store.moveDeal(deal.value.id, wonStage.value.id);
    wonOpen.value = false;
    await refresh();
  } finally {
    isSaving.value = false;
  }
};

const closeAsWon = () => {
  if (wonStage.value.requiresConfirmation) wonOpen.value = true;
  else markWon();
};

const closeAsLost = () => {
  if (
    lostStage.value.requiresConfirmation ||
    lostStage.value.requiresLossReason
  ) {
    lossOpen.value = true;
  } else {
    markLost();
  }
};

const createActivity = async type => {
  isSaving.value = true;
  try {
    activityForm.activityType = type || activityForm.activityType;
    const payload = {
      ...activityForm,
      completedAt:
        activityForm.status === 'completed'
          ? activityForm.completedAt || activityForm.occurredAt
          : '',
    };
    deal.value = await store.createActivity(deal.value.id, payload);
    activityForm.notes = '';
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_CREATED'));
  } finally {
    isSaving.value = false;
  }
};

const activityDateParts = value => {
  const dateValue = new Date(value);
  const offset = dateValue.getTimezoneOffset() * 60000;
  const localValue = new Date(dateValue.getTime() - offset)
    .toISOString()
    .slice(0, 16);
  return { date: localValue.slice(0, 10), time: localValue.slice(11, 16) };
};

const openActivityModal = (activity = null) => {
  const activityDate = activityDateParts(
    activity?.scheduledAt ||
      activity?.completedAt ||
      activity?.occurredAt ||
      new Date()
  );
  editingActivityId.value = activity?.id || null;
  activityMetadata.value = { ...(activity?.metadata || {}) };
  Object.assign(activityModalForm, {
    activityType: activity?.activityType || 'follow_up',
    subject: activity?.metadata?.subject || '',
    date: activityDate.date,
    time: activityDate.time,
    durationMinutes: activity?.metadata?.durationMinutes || 30,
    description: activity?.notes || '',
    status: activity?.status || 'pending',
    assignedUserId: activity?.user?.id || deal.value.assignedUserId || '',
    location: activity?.metadata?.location || '',
    companyId: activity?.metadata?.companyId || deal.value.company?.id || '',
    companyName:
      activity?.metadata?.companyName || deal.value.company?.name || '',
    contactId: activity?.metadata?.contactId || deal.value.contact?.id || '',
    contactName:
      activity?.metadata?.contactName || deal.value.contact?.name || '',
    invitedUserIds: activity?.metadata?.invitedUserIds || [],
    externalInvitees: activity?.metadata?.externalInvitees || '',
  });
  activityModalOpen.value = true;
};

const saveActivity = async () => {
  isSaving.value = true;
  try {
    const occurredAt = `${activityModalForm.date}T${activityModalForm.time}`;
    const isCompleted = activityModalForm.status === 'completed';
    const attributes = {
      activityType: activityModalForm.activityType,
      status: activityModalForm.status,
      occurredAt,
      scheduledAt: isCompleted ? null : occurredAt,
      completedAt: isCompleted ? occurredAt : null,
      notes: activityModalForm.description,
      userId: activityModalForm.assignedUserId || null,
      metadata: {
        ...activityMetadata.value,
        subject: activityModalForm.subject,
        durationMinutes: activityModalForm.durationMinutes,
        location: activityModalForm.location,
        companyId: activityModalForm.companyId,
        companyName: activityModalForm.companyName,
        contactId: activityModalForm.contactId,
        contactName: activityModalForm.contactName,
        invitedUserIds: activityModalForm.invitedUserIds,
        externalInvitees: activityModalForm.externalInvitees,
      },
    };
    deal.value = editingActivityId.value
      ? await store.updateActivity(
          deal.value.id,
          editingActivityId.value,
          attributes
        )
      : await store.createActivity(deal.value.id, attributes);
    activityModalOpen.value = false;
    useAlert(
      editingActivityId.value
        ? t('COMMERCIAL.MESSAGES.ACTIVITY_UPDATED')
        : t('COMMERCIAL.MESSAGES.ACTIVITY_CREATED')
    );
    editingActivityId.value = null;
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const completeActivity = async activity => {
  isSaving.value = true;
  try {
    deal.value = await store.updateActivity(deal.value.id, activity.id, {
      status: 'completed',
      completedAt: new Date().toISOString(),
      scheduledAt: null,
    });
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_COMPLETED'));
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const cancelActivity = async () => {
  const activity = cancelActivityCandidate.value;
  if (!activity) return;
  isSaving.value = true;
  try {
    deal.value = await store.updateActivity(deal.value.id, activity.id, {
      status: 'cancelled',
      scheduledAt: null,
    });
    cancelActivityCandidate.value = null;
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_CANCELLED'));
  } catch {
    useAlert(t('COMMERCIAL.MESSAGES.ACTIVITY_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const createComment = async () => {
  if (!commentBody.value.trim()) return;
  deal.value = await store.createComment(
    deal.value.id,
    commentBody.value.trim()
  );
  commentBody.value = '';
};

const documentCategory = () =>
  ({
    documents: 'document',
    products: 'product',
    orders: 'order',
  })[activeTab.value];

const createDocument = async () => {
  if (!documentForm.name.trim()) return;
  documentForm.category = documentCategory();
  deal.value = await store.createDocument(
    deal.value.id,
    documentForm,
    selectedFile.value
  );
  documentForm.name = '';
  documentForm.metadata = {};
  selectedFile.value = null;
};

const entryTitle = entry => {
  if (entry.type === 'stage_change') {
    return entry.fromStage
      ? t('COMMERCIAL.DETAIL.TIMELINE.STAGE_CHANGED_FROM', {
          from: stageLabel(entry.fromStage),
          stage: stageLabel(entry.toStage),
        })
      : t('COMMERCIAL.DETAIL.TIMELINE.STAGE_CHANGED', {
          stage: stageLabel(entry.toStage),
        });
  }
  if (entry.type === 'activity') {
    if (entry.metadata?.source === 'commercial_quote') {
      return entry.metadata.quoteNumber || entry.notes;
    }
    return entry.metadata?.subject || activityLabels.value[entry.subtype];
  }
  return entry.name || t('COMMERCIAL.DETAIL.TIMELINE.COMMENT');
};

const entryType = entry => {
  if (entry.type === 'stage_change') {
    return t('COMMERCIAL.DETAIL.TIMELINE.STAGE');
  }
  if (entry.type === 'comment') return t('COMMERCIAL.DETAIL.TIMELINE.COMMENT');
  if (entry.type === 'activity') {
    if (entry.metadata?.source === 'commercial_quote') {
      return t('COMMERCIAL.DETAIL.TIMELINE.QUOTATION');
    }
    return entry.subtype === 'email'
      ? t('COMMERCIAL.DETAIL.TIMELINE.EMAIL')
      : t('COMMERCIAL.DETAIL.TIMELINE.ACTIVITY');
  }
  return {
    document: t('COMMERCIAL.DETAIL.TIMELINE.DOCUMENT'),
    quotation: t('COMMERCIAL.DETAIL.TIMELINE.QUOTATION'),
    product: t('COMMERCIAL.DETAIL.TIMELINE.PRODUCT'),
    order: t('COMMERCIAL.DETAIL.TIMELINE.ORDER'),
  }[entry.subtype];
};

const entryDescription = entry => {
  if (entry.type === 'activity') {
    if (entry.metadata?.source === 'commercial_quote') {
      return t('COMMERCIAL.DETAIL.TIMELINE.QUOTE_EVENT', {
        status:
          quoteStatusLabels.value[entry.metadata.status] ||
          entry.metadata.status,
      });
    }
    return entry.notes || activityStatusLabels.value[entry.status];
  }
  if (entry.type === 'comment') return entry.body;
  if (entry.type === 'document') {
    return [entry.metadata?.number, entry.metadata?.status]
      .filter(Boolean)
      .join(' · ');
  }
  if (entry.metadata?.source === 'commercial_quote') {
    return 'i-lucide-receipt-text';
  }
  return null;
};

const entryIcon = entry => {
  if (entry.type === 'stage_change') return 'i-lucide-git-commit-horizontal';
  if (entry.type === 'comment') return 'i-lucide-message-square-text';
  if (entry.type === 'document') {
    return entry.subtype === 'quotation'
      ? 'i-lucide-file-check-2'
      : 'i-lucide-paperclip';
  }
  return entry.subtype === 'email'
    ? 'i-lucide-mail'
    : 'i-lucide-calendar-check';
};

const openCompany = () =>
  router.push({
    name: 'companies_dashboard_show',
    params: { companyId: deal.value.company.id },
  });

const openContacts = action => {
  contactsModalOpen.value = false;
  router.push({ name: 'contacts_dashboard_index', query: { action } });
};

onMounted(loadDeal);
</script>

<template>
  <main
    class="isolate flex h-full min-w-0 max-w-full flex-1 basis-0 self-stretch flex-col overflow-x-hidden bg-n-background"
  >
    <template v-if="deal">
      <header
        class="w-full min-w-0 max-w-full shrink-0 border-b border-n-weak bg-n-alpha-1 px-4 py-3 sm:px-5"
      >
        <button
          type="button"
          class="mb-2 inline-flex items-center gap-2 text-sm text-n-brand hover:text-n-brand/80"
          @click="router.push({ name: 'deals_pipeline' })"
        >
          <span class="i-lucide-arrow-left size-4" />
          {{ t('COMMERCIAL.DETAIL.BACK') }}
        </button>
        <div
          class="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="mt-0.5 grid size-10 shrink-0 place-content-center rounded-xl bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-hand-coins size-5" />
            </span>
            <div class="min-w-0">
              <h1 class="break-words text-xl font-semibold text-n-slate-12">
                {{ deal.name }}
              </h1>
              <strong class="mt-0.5 block text-base text-n-slate-12">
                {{ money(deal.amount, deal.currency) }}
              </strong>
              <div
                class="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-n-slate-10"
              >
                <span class="inline-flex items-center gap-1.5">
                  <span class="i-lucide-user-round size-4" />
                  {{ deal.contact?.name || '—' }}
                </span>
                <span class="inline-flex items-center gap-1.5">
                  <span class="i-lucide-building-2 size-4" />
                  {{ deal.company.name }}
                </span>
              </div>
            </div>
          </div>
          <div class="flex min-w-0 flex-wrap items-center gap-2 lg:justify-end">
            <button
              v-if="deal.status === 'open' && wonStage"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-teal-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-teal-10"
              @click="closeAsWon"
            >
              <span class="i-lucide-trophy size-4" />
              {{ t('COMMERCIAL.DETAIL.WON') }}
            </button>
            <button
              v-if="deal.status === 'open' && lostStage"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-ruby-10"
              @click="closeAsLost"
            >
              <span class="i-lucide-circle-x size-4" />
              {{ t('COMMERCIAL.DETAIL.LOST') }}
            </button>
            <span
              class="ms-1 inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-n-slate-10"
            >
              <span class="i-lucide-calendar-days size-4" />
              {{ date(deal.expectedCloseDate) }}
            </span>
            <button
              v-if="deal.status === 'lost'"
              type="button"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12"
              @click="store.reactivate(deal.id).then(refresh)"
            >
              {{ t('COMMERCIAL.DETAIL.REACTIVATE') }}
            </button>
          </div>
        </div>
        <div class="mt-4 w-full min-w-0 max-w-full overflow-x-auto pb-1">
          <div
            class="flex w-full min-w-max items-stretch overflow-hidden rounded-lg bg-n-alpha-2 p-1"
          >
            <button
              v-for="stage in visibleStages"
              :key="stage.id"
              type="button"
              class="relative -ms-3 min-w-48 flex-1 cursor-pointer py-3 pe-6 ps-8 text-left transition-colors first:ms-0 first:ps-5 hover:z-20 focus:z-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-wait [clip-path:polygon(0_0,92%_0,100%_50%,92%_100%,0_100%,8%_50%)] first:[clip-path:polygon(0_0,92%_0,100%_50%,92%_100%,0_100%)]"
              :class="
                stage.id === deal.currentStageId
                  ? 'z-10 bg-n-teal-9 text-white'
                  : stage.position < currentStagePosition ||
                      ['won', 'lost'].includes(deal.status)
                    ? 'bg-n-teal-11/60 text-white'
                    : 'bg-n-slate-5 text-n-slate-11 hover:bg-n-slate-6'
              "
              :disabled="isSaving"
              @click="moveToStage(stage)"
            >
              <span class="block truncate text-xs font-semibold">
                {{ stageLabel(stage) }}
              </span>
              <span
                v-if="
                  hasStageDuration(stage) || stage.id === deal.currentStageId
                "
                class="mt-1 block text-xs opacity-80"
              >
                {{
                  t('COMMERCIAL.DETAIL.STAGE_DAYS', {
                    count: stageDurationById[stage.id] || 0,
                  })
                }}
              </span>
            </button>
          </div>
        </div>
      </header>

      <div
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto 2xl:flex-row 2xl:overflow-hidden"
      >
        <section
          class="flex min-w-0 max-w-full flex-none flex-col 2xl:min-h-0 2xl:flex-1"
        >
          <nav
            class="relative z-10 flex w-full min-w-0 max-w-full shrink-0 overflow-x-auto border-b border-n-weak px-4"
          >
            <button
              v-for="tab in tabs"
              :key="tab[0]"
              type="button"
              class="shrink-0 whitespace-nowrap border-b-2 px-3 py-3 text-sm"
              :class="
                activeTab === tab[0]
                  ? 'border-n-brand text-n-brand'
                  : 'border-transparent text-n-slate-10'
              "
              @click="activeTab = tab[0]"
            >
              {{ tab[1] }}
            </button>
          </nav>

          <div
            class="min-w-0 max-w-full flex-1 p-4 sm:p-5 2xl:min-h-0 2xl:overflow-y-auto"
          >
            <section v-if="activeTab === 'timeline'" class="mx-auto max-w-4xl">
              <div
                v-if="!deal.timeline.length"
                class="rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-12 text-center"
              >
                <span
                  class="i-lucide-history mx-auto block size-8 text-n-slate-8"
                />
                <h2 class="mt-3 text-sm font-semibold text-n-slate-12">
                  {{ t('COMMERCIAL.DETAIL.TIMELINE.EMPTY_TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-9">
                  {{ t('COMMERCIAL.DETAIL.TIMELINE.EMPTY_DESCRIPTION') }}
                </p>
              </div>
              <ol v-else class="space-y-0">
                <li
                  v-for="(entry, index) in deal.timeline"
                  :key="`${entry.type}-${entry.id}`"
                  class="relative flex gap-4 pb-6"
                >
                  <span
                    v-if="index < deal.timeline.length - 1"
                    class="absolute start-4 top-8 h-full w-px bg-n-weak"
                  />
                  <span
                    class="relative z-10 grid size-8 shrink-0 place-content-center rounded-full border border-n-weak bg-n-background text-n-brand"
                  >
                    <span class="size-4" :class="entryIcon(entry)" />
                  </span>
                  <article
                    class="min-w-0 flex-1 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                  >
                    <div
                      class="flex flex-wrap items-start justify-between gap-2"
                    >
                      <div>
                        <span
                          class="text-xs font-semibold uppercase tracking-wide text-n-brand"
                        >
                          {{ entryType(entry) }}
                        </span>
                        <h3 class="mt-1 text-sm font-semibold text-n-slate-12">
                          {{ entryTitle(entry) }}
                        </h3>
                      </div>
                      <time class="text-xs text-n-slate-9">
                        {{ dateTime(entry.occurredAt) }}
                      </time>
                    </div>
                    <p
                      v-if="entryDescription(entry)"
                      class="mt-2 whitespace-pre-wrap text-sm text-n-slate-11"
                    >
                      {{ entryDescription(entry) }}
                    </p>
                    <p
                      class="mt-3 flex items-center gap-1.5 text-xs text-n-slate-9"
                    >
                      <span class="i-lucide-user-round size-3.5" />
                      {{
                        t('COMMERCIAL.DETAIL.TIMELINE.BY_USER', {
                          user:
                            entry.user?.name ||
                            t('COMMERCIAL.DETAIL.TIMELINE.SYSTEM_USER'),
                        })
                      }}
                    </p>
                  </article>
                </li>
              </ol>
            </section>

            <section
              v-else-if="activeTab === 'activities'"
              class="mx-auto max-w-4xl space-y-8"
            >
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 class="text-lg font-semibold text-n-slate-12">
                    {{ t('COMMERCIAL.DETAIL.ACTIVITIES.PLANNED') }}
                  </h2>
                  <p class="mt-1 text-sm text-n-slate-9">
                    {{ t('COMMERCIAL.DETAIL.ACTIVITIES.PLANNED_DESCRIPTION') }}
                  </p>
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand/90"
                  @click="openActivityModal"
                >
                  <span class="i-lucide-plus size-4" />
                  {{ t('COMMERCIAL.DETAIL.ACTIVITIES.ADD') }}
                </button>
              </div>

              <div
                v-if="plannedActivities.length"
                class="grid gap-3 md:grid-cols-2"
              >
                <article
                  v-for="activity in plannedActivities"
                  :key="activity.id"
                  class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <span class="text-xs font-medium text-n-brand">
                        {{ activityLabels[activity.activityType] }}
                      </span>
                      <h3 class="mt-1 truncate font-semibold text-n-slate-12">
                        {{
                          activity.metadata?.subject ||
                          activityLabels[activity.activityType]
                        }}
                      </h3>
                    </div>
                    <span
                      class="rounded-full bg-n-amber-3 px-2 py-1 text-xs text-n-amber-11"
                    >
                      {{ activityStatusLabels[activity.status] }}
                    </span>
                  </div>
                  <p
                    class="mt-3 flex items-center gap-2 text-sm text-n-slate-10"
                  >
                    <span class="i-lucide-calendar-clock size-4" />
                    {{ dateTime(activity.scheduledAt || activity.occurredAt) }}
                    <span v-if="activity.metadata?.durationMinutes">
                      {{
                        t('COMMERCIAL.DETAIL.ACTIVITIES.DURATION_VALUE', {
                          count: activity.metadata.durationMinutes,
                        })
                      }}
                    </span>
                  </p>
                  <p
                    v-if="activity.notes"
                    class="mt-3 whitespace-pre-wrap text-sm text-n-slate-11"
                  >
                    {{ activity.notes }}
                  </p>
                  <div
                    class="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-n-slate-9"
                  >
                    <span class="inline-flex items-center gap-1">
                      <span class="i-lucide-user-round size-3.5" />
                      {{ activity.user?.name || t('COMMERCIAL.UNASSIGNED') }}
                    </span>
                    <span
                      v-if="activity.metadata?.location"
                      class="inline-flex items-center gap-1"
                    >
                      <span class="i-lucide-map-pin size-3.5" />
                      {{ activity.metadata.location }}
                    </span>
                  </div>
                  <div
                    class="mt-4 flex flex-wrap gap-2 border-t border-n-weak pt-3"
                  >
                    <button
                      type="button"
                      :disabled="isSaving"
                      class="inline-flex items-center gap-1.5 rounded-lg bg-n-teal-9 px-3 py-1.5 text-xs font-medium text-white hover:bg-n-teal-10 disabled:opacity-50"
                      @click="completeActivity(activity)"
                    >
                      <span class="i-lucide-circle-check size-3.5" />
                      {{ t('COMMERCIAL.DETAIL.ACTIVITIES.COMPLETE_ACTION') }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                      @click="openActivityModal(activity)"
                    >
                      <span class="i-lucide-calendar-clock size-3.5" />
                      {{ t('COMMERCIAL.DETAIL.ACTIVITIES.RESCHEDULE') }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                      @click="openActivityModal(activity)"
                    >
                      <span class="i-lucide-pencil size-3.5" />
                      {{ t('COMMERCIAL.DETAIL.ACTIVITIES.EDIT_ACTION') }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1.5 rounded-lg border border-n-ruby-7 px-3 py-1.5 text-xs text-n-ruby-11 hover:bg-n-ruby-3"
                      @click="cancelActivityCandidate = activity"
                    >
                      <span class="i-lucide-ban size-3.5" />
                      {{ t('COMMERCIAL.DETAIL.ACTIVITIES.CANCEL_ACTION') }}
                    </button>
                  </div>
                </article>
              </div>
              <div
                v-else
                class="rounded-xl border border-dashed border-n-weak px-6 py-10 text-center text-sm text-n-slate-9"
              >
                {{ t('COMMERCIAL.DETAIL.ACTIVITIES.EMPTY_PLANNED') }}
              </div>

              <div class="border-t border-n-weak pt-7">
                <h2 class="text-lg font-semibold text-n-slate-12">
                  {{ t('COMMERCIAL.DETAIL.ACTIVITIES.COMPLETED_ACTIVITIES') }}
                </h2>
                <h3
                  class="mt-5 text-sm font-semibold uppercase tracking-wide text-n-slate-9"
                >
                  {{ t('COMMERCIAL.DETAIL.ACTIVITIES.COMPLETED') }}
                </h3>
              </div>
              <div v-if="completedActivities.length" class="space-y-3">
                <article
                  v-for="activity in completedActivities"
                  :key="activity.id"
                  class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                >
                  <div class="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span class="text-xs font-medium text-n-teal-11">
                        {{ activityLabels[activity.activityType] }}
                      </span>
                      <h3 class="mt-1 font-semibold text-n-slate-12">
                        {{
                          activity.metadata?.subject ||
                          activityLabels[activity.activityType]
                        }}
                      </h3>
                    </div>
                    <div class="flex flex-col items-end gap-1.5">
                      <span
                        class="rounded-full px-2 py-1 text-xs"
                        :class="
                          activity.status === 'completed'
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-slate-3 text-n-slate-11'
                        "
                      >
                        {{ activityStatusLabels[activity.status] }}
                      </span>
                      <time class="text-xs text-n-slate-9">
                        {{
                          dateTime(activity.completedAt || activity.occurredAt)
                        }}
                      </time>
                    </div>
                  </div>
                  <p
                    v-if="activity.notes"
                    class="mt-2 whitespace-pre-wrap text-sm text-n-slate-11"
                  >
                    {{ activity.notes }}
                  </p>
                  <p class="mt-3 text-xs text-n-slate-9">
                    {{ activity.user?.name || t('COMMERCIAL.UNASSIGNED') }}
                  </p>
                  <button
                    type="button"
                    class="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-2"
                    @click="openActivityModal(activity)"
                  >
                    <span class="i-lucide-eye size-3.5" />
                    {{ t('COMMERCIAL.DETAIL.ACTIVITIES.VIEW_DETAIL') }}
                  </button>
                </article>
              </div>
              <div
                v-else
                class="rounded-xl border border-dashed border-n-weak px-6 py-10 text-center text-sm text-n-slate-9"
              >
                {{ t('COMMERCIAL.DETAIL.ACTIVITIES.EMPTY_COMPLETED') }}
              </div>
            </section>

            <section
              v-else-if="activeTab === 'emails'"
              class="mx-auto max-w-3xl space-y-5"
            >
              <form
                class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 sm:grid-cols-2"
                @submit.prevent="createActivity('email')"
              >
                <select
                  v-model="activityForm.status"
                  class="h-9 rounded-lg border border-n-weak bg-n-background px-2 text-sm"
                >
                  <option value="pending">
                    {{ t('COMMERCIAL.ACTIVITY_STATUS.PENDING') }}
                  </option>
                  <option value="completed">
                    {{ t('COMMERCIAL.ACTIVITY_STATUS.COMPLETED') }}
                  </option>
                  <option value="cancelled">
                    {{ t('COMMERCIAL.ACTIVITY_STATUS.CANCELLED') }}
                  </option>
                </select>
                <label class="text-xs text-n-slate-10">
                  {{ t('COMMERCIAL.DETAIL.FORMS.DATE_TIME') }}
                  <input
                    v-model="activityForm.occurredAt"
                    required
                    type="datetime-local"
                    class="mt-1 block h-9 w-full rounded-lg border border-n-weak bg-n-background px-2 text-sm"
                  />
                </label>
                <label class="text-xs text-n-slate-10">
                  {{ t('COMMERCIAL.DETAIL.FORMS.SCHEDULED_AT') }}
                  <input
                    v-model="activityForm.scheduledAt"
                    type="datetime-local"
                    class="mt-1 block h-9 w-full rounded-lg border border-n-weak bg-n-background px-2 text-sm"
                  />
                </label>
                <textarea
                  v-model="activityForm.notes"
                  required
                  rows="3"
                  class="sm:col-span-2 rounded-lg border border-n-weak bg-n-background p-3 text-sm"
                  :placeholder="t('COMMERCIAL.DETAIL.FORMS.NOTES')"
                />
                <button
                  type="submit"
                  class="sm:col-span-2 justify-self-end rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
                >
                  {{ t('COMMERCIAL.DETAIL.FORMS.SAVE') }}
                </button>
              </form>
              <article
                v-for="activity in deal.activities.filter(
                  item => item.activityType === 'email'
                )"
                :key="activity.id"
                class="rounded-xl border border-n-weak p-4"
              >
                <div class="flex justify-between gap-3">
                  <strong class="text-sm text-n-slate-12">
                    {{ activityLabels[activity.activityType] }}
                  </strong>
                  <span class="text-xs text-n-slate-9">
                    {{ dateTime(activity.occurredAt) }}
                  </span>
                </div>
                <p class="mt-2 whitespace-pre-wrap text-sm text-n-slate-11">
                  {{ activity.notes }}
                </p>
                <span
                  class="mt-2 inline-block rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-10"
                >
                  {{ activityStatusLabels[activity.status] }}
                </span>
              </article>
            </section>

            <CommercialQuotesPanel
              v-else-if="activeTab === 'quotations'"
              :deal-id="deal.id"
              :default-currency="deal.currency"
              @changed="refresh"
            />

            <section
              v-else-if="
                ['documents', 'products', 'orders'].includes(activeTab)
              "
              class="mx-auto max-w-3xl space-y-5"
            >
              <form
                class="grid gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-4 sm:grid-cols-2"
                @submit.prevent="createDocument"
              >
                <input
                  v-model="documentForm.name"
                  required
                  class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm sm:col-span-2"
                  :placeholder="documentNamePlaceholder"
                />
                <template v-if="activeTab === 'products'">
                  <input
                    v-model="documentForm.metadata.quantity"
                    type="number"
                    min="0"
                    class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                    :placeholder="t('COMMERCIAL.DETAIL.FORMS.QUANTITY')"
                  />
                  <input
                    v-model="documentForm.metadata.unit_price"
                    type="number"
                    min="0"
                    class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                    :placeholder="t('COMMERCIAL.DETAIL.FORMS.UNIT_PRICE')"
                  />
                </template>
                <template v-if="activeTab === 'orders'">
                  <input
                    v-model="documentForm.metadata.number"
                    class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                    :placeholder="t('COMMERCIAL.DETAIL.FORMS.NUMBER')"
                  />
                  <input
                    v-model="documentForm.metadata.total"
                    type="number"
                    min="0"
                    class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                    :placeholder="t('COMMERCIAL.DETAIL.FORMS.TOTAL')"
                  />
                  <input
                    v-model="documentForm.metadata.status"
                    class="h-9 rounded-lg border border-n-weak bg-n-background px-3 text-sm"
                    :placeholder="t('COMMERCIAL.DETAIL.FORMS.STATUS')"
                  />
                </template>
                <input
                  v-if="activeTab !== 'products'"
                  type="file"
                  class="text-sm text-n-slate-10 sm:col-span-2"
                  @change="selectedFile = $event.target.files[0]"
                />
                <button
                  type="submit"
                  class="sm:col-span-2 justify-self-end rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
                >
                  {{ t('COMMERCIAL.DETAIL.FORMS.SAVE') }}
                </button>
              </form>
              <article
                v-for="record in recordsForTab"
                :key="record.id"
                class="rounded-xl border border-n-weak p-4"
              >
                <div class="flex items-start justify-between gap-3">
                  <strong class="text-sm text-n-slate-12">
                    {{ record.name }}
                  </strong>
                  <time class="text-xs text-n-slate-9">
                    {{ dateTime(record.occurredAt) }}
                  </time>
                </div>
                <dl
                  v-if="Object.keys(record.metadata || {}).length"
                  class="mt-3 grid grid-cols-2 gap-2 text-xs"
                >
                  <template v-for="(value, key) in record.metadata" :key="key">
                    <dt class="text-n-slate-9">{{ key }}</dt>
                    <dd class="text-n-slate-12">{{ value }}</dd>
                  </template>
                </dl>
                <a
                  v-if="record.fileUrl"
                  :href="record.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mt-3 inline-flex items-center gap-1 text-sm text-n-brand"
                >
                  <span class="i-lucide-download size-4" />
                  {{ t('COMMERCIAL.DETAIL.FORMS.OPEN_FILE') }}
                </a>
              </article>
            </section>

            <section
              v-else-if="activeTab === 'companies'"
              class="mx-auto max-w-3xl rounded-xl border border-n-weak p-5"
            >
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ deal.company.name }}
              </h2>
              <p class="mt-1 text-sm text-n-slate-10">
                {{ deal.company.domain }}
              </p>
              <p class="mt-3 whitespace-pre-wrap text-sm text-n-slate-11">
                {{ deal.company.description }}
              </p>
              <button
                type="button"
                class="mt-4 rounded-lg bg-n-brand px-4 py-2 text-sm text-white"
                @click="openCompany"
              >
                {{ t('COMMERCIAL.DETAIL.OPEN_COMPANY') }}
              </button>
            </section>

            <section
              v-else-if="activeTab === 'comments'"
              class="mx-auto max-w-3xl space-y-4"
            >
              <form
                class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
                @submit.prevent="createComment"
              >
                <textarea
                  v-model="commentBody"
                  required
                  rows="3"
                  class="w-full rounded-lg border border-n-weak bg-n-background p-3 text-sm"
                  :placeholder="t('COMMERCIAL.DETAIL.FORMS.COMMENT')"
                />
                <button
                  type="submit"
                  class="mt-2 rounded-lg bg-n-brand px-4 py-2 text-sm text-white"
                >
                  {{ t('COMMERCIAL.DETAIL.FORMS.SAVE') }}
                </button>
              </form>
              <article
                v-for="comment in deal.comments"
                :key="comment.id"
                class="rounded-xl border border-n-weak p-4"
              >
                <div class="flex justify-between gap-3 text-xs text-n-slate-9">
                  <span>{{ comment.user.name }}</span>
                  <time>{{ dateTime(comment.occurredAt) }}</time>
                </div>
                <p class="mt-2 whitespace-pre-wrap text-sm text-n-slate-12">
                  {{ comment.body }}
                </p>
              </article>
            </section>

            <section v-else class="mx-auto max-w-3xl space-y-3">
              <article
                v-for="entry in deal.timeline.filter(
                  item => item.type === 'stage_change'
                )"
                :key="entry.id"
                class="rounded-xl border border-n-weak p-4"
              >
                <strong class="text-sm text-n-slate-12">{{
                  entryTitle(entry)
                }}</strong>
                <p class="mt-1 text-xs text-n-slate-9">
                  {{ updateMeta(entry) }}
                </p>
              </article>
            </section>
          </div>
        </section>

        <aside
          class="w-full min-w-0 max-w-full shrink-0 border-t border-n-weak bg-n-alpha-1 p-4 sm:p-5 2xl:w-80 2xl:overflow-y-auto 2xl:border-s 2xl:border-t-0"
        >
          <div class="flex items-center justify-between">
            <h2 class="font-semibold text-n-slate-12">
              {{ t('COMMERCIAL.DETAIL.SUMMARY') }}
            </h2>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="text-sm text-n-slate-10 hover:text-n-brand"
                @click="detailsModalOpen = true"
              >
                {{ t('COMMERCIAL.DETAIL.SEE_MORE') }}
              </button>
              <button
                type="button"
                class="text-sm text-n-brand"
                @click="isEditing = !isEditing"
              >
                {{ t('COMMERCIAL.DETAIL.EDIT') }}
              </button>
            </div>
          </div>
          <form
            v-if="isEditing"
            class="mt-4 space-y-3"
            @submit.prevent="saveDeal"
          >
            <input
              v-model="editForm.name"
              required
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm"
            />
            <input
              v-model.number="editForm.amount"
              type="number"
              min="0"
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm"
            />
            <select
              v-model="editForm.assignedUserId"
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-2 text-sm"
            >
              <option value="">{{ t('COMMERCIAL.UNASSIGNED') }}</option>
              <option
                v-for="agent in store.agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
            <input
              v-model="editForm.expectedCloseDate"
              type="date"
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm"
            />
            <input
              v-model="editForm.source"
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm"
              :placeholder="t('COMMERCIAL.FIELDS.SOURCE')"
            />
            <input
              v-model="editForm.businessLine"
              class="h-9 w-full rounded-lg border border-n-weak bg-n-background px-3 text-sm"
              :placeholder="t('COMMERCIAL.FIELDS.BUSINESS_LINE')"
            />
            <textarea
              v-model="editForm.description"
              rows="3"
              class="w-full rounded-lg border border-n-weak bg-n-background p-3 text-sm"
            />
            <button
              type="submit"
              class="w-full rounded-lg bg-n-brand px-4 py-2 text-sm text-white"
            >
              {{ t('COMMERCIAL.DETAIL.FORMS.SAVE') }}
            </button>
          </form>
          <dl v-else class="mt-4 min-w-0 divide-y divide-n-weak text-sm">
            <div
              class="grid min-w-0 grid-cols-1 gap-3 py-3 first:pt-0 sm:grid-cols-2 2xl:grid-cols-1"
            >
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.DETAIL.ORIGIN') }}
                </dt>
                <dd class="mt-1 break-words text-n-slate-12">
                  {{ deal.source || '—' }}
                </dd>
              </div>
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.FIELDS.BUSINESS_LINE') }}
                </dt>
                <dd class="mt-1 break-words text-n-slate-12">
                  {{ deal.businessLine || '—' }}
                </dd>
              </div>
            </div>
            <div
              class="grid min-w-0 grid-cols-1 gap-3 py-3 sm:grid-cols-2 2xl:grid-cols-1"
            >
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.DETAIL.ASSIGNED_TO') }}
                </dt>
                <dd class="mt-1 break-words text-n-slate-12">
                  {{ deal.assignedUser?.name || t('COMMERCIAL.UNASSIGNED') }}
                </dd>
              </div>
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.DETAIL.WARRANTY_DATE') }}
                </dt>
                <dd class="mt-1 break-words text-n-slate-12">
                  {{ date(warrantyDate) }}
                </dd>
              </div>
            </div>
            <div
              class="grid min-w-0 grid-cols-1 gap-3 py-3 sm:grid-cols-2 2xl:grid-cols-1"
            >
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.INACTIVITY') }}
                </dt>
                <dd
                  class="mt-1"
                  :class="
                    deal.inactivityDays >= 7
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{
                    t('COMMERCIAL.INACTIVITY_DAYS', {
                      count: deal.inactivityDays,
                    })
                  }}
                </dd>
              </div>
              <div class="min-w-0">
                <dt class="text-xs text-n-slate-9">
                  {{ t('COMMERCIAL.FIELDS.PROBABILITY') }}
                </dt>
                <dd class="mt-1 text-n-slate-12">
                  {{
                    t('COMMERCIAL.PROBABILITY_VALUE', {
                      count: deal.probability,
                    })
                  }}
                </dd>
              </div>
            </div>
            <div class="min-w-0 py-3">
              <dt class="text-xs text-n-slate-9">
                {{ t('COMMERCIAL.DETAIL.DESCRIPTION') }}
              </dt>
              <dd class="mt-1 whitespace-pre-wrap break-words text-n-slate-12">
                {{ deal.description || '—' }}
              </dd>
            </div>
            <div class="min-w-0 py-3">
              <dt class="text-xs text-n-slate-9">
                {{ t('COMMERCIAL.FIELDS.SOURCE') }}
              </dt>
              <dd class="mt-1 break-words text-n-slate-12">
                {{ companySource || '—' }}
              </dd>
            </div>
            <div class="min-w-0 py-3">
              <dt class="text-xs text-n-slate-9">
                {{ t('COMMERCIAL.DETAIL.RELATED_CONTACTS') }}
              </dt>
              <dd
                class="mt-2 min-w-0 rounded-lg border border-n-weak bg-n-background p-3"
              >
                <span class="block break-words font-medium text-n-slate-12">
                  {{ deal.contact?.name || '—' }}
                </span>
                <span class="mt-1 block break-all text-xs text-n-slate-9">
                  {{ deal.contact?.email || deal.contact?.phoneNumber || '—' }}
                </span>
                <button
                  type="button"
                  class="mt-3 text-xs font-medium text-n-brand"
                  @click="contactsModalOpen = true"
                >
                  {{ t('COMMERCIAL.DETAIL.SEE_MORE') }}
                </button>
              </dd>
            </div>
            <div class="min-w-0 py-3">
              <dt class="text-xs text-n-slate-9">
                {{ t('COMMERCIAL.DETAIL.RELATED_COMPANY') }}
              </dt>
              <div
                class="mt-2 w-full min-w-0 rounded-lg border border-n-weak bg-n-background p-3"
              >
                <span class="block break-words font-medium text-n-slate-12">
                  {{ deal.company.name }}
                </span>
                <dl class="mt-3 grid min-w-0 gap-2 text-xs">
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.DETAIL.COMPANY.NIT') }}
                    </dt>
                    <dd class="break-words text-n-slate-11">
                      {{ customValue(deal.company, 'nit') }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.DETAIL.COMPANY.ACTIVITY') }}
                    </dt>
                    <dd class="break-words text-n-slate-11">
                      {{ customValue(deal.company, 'economic_activity') }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.DETAIL.COMPANY.EMAIL') }}
                    </dt>
                    <dd class="break-all text-n-slate-11">
                      {{ customValue(deal.company, 'email') }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.DETAIL.ASSIGNED_TO') }}
                    </dt>
                    <dd class="break-words text-n-slate-11">
                      {{
                        deal.assignedUser?.name || t('COMMERCIAL.UNASSIGNED')
                      }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.DETAIL.COMPANY.COUNTRY') }}
                    </dt>
                    <dd class="break-words text-n-slate-11">
                      {{ customValue(deal.company, 'country', 'country_name') }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="text-n-slate-9">
                      {{ t('COMMERCIAL.FIELDS.SOURCE') }}
                    </dt>
                    <dd class="break-words text-n-slate-11">
                      {{ companySource || '—' }}
                    </dd>
                  </div>
                </dl>
                <button
                  type="button"
                  class="mt-3 text-xs font-medium text-n-brand"
                  @click="companyModalOpen = true"
                >
                  {{ t('COMMERCIAL.DETAIL.SEE_MORE') }}
                </button>
              </div>
            </div>
          </dl>
        </aside>
      </div>
    </template>

    <div
      v-if="detailsModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      @click.self="detailsModalOpen = false"
    >
      <section
        class="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-n-weak bg-n-background shadow-2xl"
      >
        <header
          class="sticky top-0 flex items-center justify-between border-b border-n-weak bg-n-background px-5 py-4"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('COMMERCIAL.DETAIL.DEAL_DETAILS') }}
          </h2>
          <button
            type="button"
            class="text-n-slate-9"
            @click="detailsModalOpen = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </header>
        <dl class="grid gap-x-6 gap-y-5 p-5 sm:grid-cols-2">
          <div
            v-for="item in [
              [t('COMMERCIAL.DETAIL.ORIGIN'), deal.source],
              [t('COMMERCIAL.FIELDS.BUSINESS_LINE'), deal.businessLine],
              [t('COMMERCIAL.DETAIL.ASSIGNED_TO'), deal.assignedUser?.name],
              [t('COMMERCIAL.DETAIL.WARRANTY_DATE'), date(warrantyDate)],
              [
                t('COMMERCIAL.INACTIVITY'),
                t('COMMERCIAL.INACTIVITY_DAYS', { count: deal.inactivityDays }),
              ],
              [t('COMMERCIAL.FIELDS.SOURCE'), companySource],
              [
                t('COMMERCIAL.FIELDS.PROBABILITY'),
                t('COMMERCIAL.PROBABILITY_VALUE', { count: deal.probability }),
              ],
              [t('COMMERCIAL.FIELDS.COMPANY'), deal.company.name],
              [t('COMMERCIAL.FIELDS.CONTACT'), deal.contact?.name],
            ]"
            :key="item[0]"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-9">{{ item[0] }}</dt>
            <dd class="mt-1 break-words text-sm text-n-slate-12">
              {{ item[1] || '—' }}
            </dd>
          </div>
          <div class="min-w-0 sm:col-span-2">
            <dt class="text-xs text-n-slate-9">
              {{ t('COMMERCIAL.DETAIL.DESCRIPTION') }}
            </dt>
            <dd
              class="mt-1 whitespace-pre-wrap break-words text-sm text-n-slate-12"
            >
              {{ deal.description || '—' }}
            </dd>
          </div>
        </dl>
        <footer class="flex justify-end border-t border-n-weak px-5 py-4">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="detailsModalOpen = false"
          >
            {{ t('COMMERCIAL.DETAIL.CLOSE') }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="contactsModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      @click.self="contactsModalOpen = false"
    >
      <section
        class="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-2xl"
      >
        <header
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('COMMERCIAL.DETAIL.RELATED_CONTACTS') }}
          </h2>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-11"
              @click="openContacts('select')"
            >
              {{ t('COMMERCIAL.DETAIL.CONTACTS.SELECT') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white"
              @click="openContacts('new')"
            >
              {{ t('COMMERCIAL.DETAIL.CONTACTS.ADD') }}
            </button>
          </div>
        </header>
        <div class="max-h-[65vh] overflow-auto">
          <table
            v-if="contactRows.length"
            class="w-full min-w-[1100px] text-left text-sm"
          >
            <thead class="sticky top-0 bg-n-alpha-2 text-xs text-n-slate-9">
              <tr>
                <th
                  v-for="heading in [
                    t('COMMERCIAL.DETAIL.CONTACTS.NAME'),
                    t('COMMERCIAL.DETAIL.CONTACTS.EMAIL'),
                    t('COMMERCIAL.DETAIL.CONTACTS.PHONE'),
                    t('COMMERCIAL.FIELDS.COMPANY'),
                    t('COMMERCIAL.DETAIL.CONTACTS.ROLE'),
                    t('COMMERCIAL.DETAIL.ASSIGNED_TO'),
                    t('COMMERCIAL.DETAIL.CONTACTS.BIRTHDAY'),
                    t('COMMERCIAL.DETAIL.CONTACTS.AREA'),
                    t('COMMERCIAL.FIELDS.SOURCE'),
                    t('COMMERCIAL.DETAIL.COMPANY.COUNTRY'),
                  ]"
                  :key="heading"
                  class="whitespace-nowrap px-4 py-3 font-medium"
                >
                  {{ heading }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contact in contactRows"
                :key="contact.id"
                class="border-t border-n-weak text-n-slate-11"
              >
                <td class="px-4 py-3 font-medium text-n-slate-12">
                  {{ contact.name || '—' }}
                </td>
                <td class="px-4 py-3">{{ contact.email || '—' }}</td>
                <td class="px-4 py-3">{{ contact.phoneNumber || '—' }}</td>
                <td class="px-4 py-3">{{ deal.company.name }}</td>
                <td class="px-4 py-3">
                  {{ customValue(contact, 'job_title', 'role') }}
                </td>
                <td class="px-4 py-3">
                  {{ deal.assignedUser?.name || t('COMMERCIAL.UNASSIGNED') }}
                </td>
                <td class="px-4 py-3">
                  {{ customValue(contact, 'date_of_birth', 'birthday') }}
                </td>
                <td class="px-4 py-3">
                  {{ customValue(contact, 'area', 'department') }}
                </td>
                <td class="px-4 py-3">{{ customValue(contact, 'source') }}</td>
                <td class="px-4 py-3">
                  {{ customValue(contact, 'country', 'country_name') }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="px-6 py-16 text-center text-sm text-n-slate-9">
            {{ t('COMMERCIAL.DETAIL.CONTACTS.EMPTY') }}
          </div>
        </div>
        <footer class="flex justify-end border-t border-n-weak px-5 py-4">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="contactsModalOpen = false"
          >
            {{ t('COMMERCIAL.DETAIL.CLOSE') }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="companyModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      @click.self="companyModalOpen = false"
    >
      <section
        class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-n-weak bg-n-background shadow-2xl"
      >
        <header
          class="flex items-center justify-between border-b border-n-weak px-5 py-4"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('COMMERCIAL.DETAIL.RELATED_COMPANY') }}
          </h2>
          <button
            type="button"
            class="text-n-slate-9"
            @click="companyModalOpen = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </header>
        <dl class="grid gap-x-6 gap-y-5 p-5 sm:grid-cols-2">
          <div
            v-for="item in [
              [t('COMMERCIAL.FIELDS.COMPANY'), deal.company.name],
              [
                t('COMMERCIAL.DETAIL.COMPANY.NIT'),
                customValue(deal.company, 'nit'),
              ],
              [
                t('COMMERCIAL.DETAIL.COMPANY.ACTIVITY'),
                customValue(deal.company, 'economic_activity'),
              ],
              [
                t('COMMERCIAL.DETAIL.COMPANY.EMAIL'),
                customValue(deal.company, 'email'),
              ],
              [t('COMMERCIAL.DETAIL.ASSIGNED_TO'), deal.assignedUser?.name],
              [
                t('COMMERCIAL.DETAIL.COMPANY.COUNTRY'),
                customValue(deal.company, 'country', 'country_name'),
              ],
              [t('COMMERCIAL.FIELDS.SOURCE'), companySource],
            ]"
            :key="item[0]"
            class="min-w-0"
          >
            <dt class="text-xs text-n-slate-9">{{ item[0] }}</dt>
            <dd class="mt-1 break-words text-sm text-n-slate-12">
              {{ item[1] || '—' }}
            </dd>
          </div>
        </dl>
        <footer class="flex justify-end border-t border-n-weak px-5 py-4">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="companyModalOpen = false"
          >
            {{ t('COMMERCIAL.DETAIL.CLOSE') }}
          </button>
        </footer>
      </section>
    </div>

    <div
      v-if="activityModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      @click.self="activityModalOpen = false"
    >
      <form
        class="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-n-weak bg-n-background shadow-2xl"
        @submit.prevent="saveActivity"
      >
        <header
          class="sticky top-0 z-10 flex items-center justify-between border-b border-n-weak bg-n-background px-5 py-4"
        >
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{
              editingActivityId
                ? t('COMMERCIAL.DETAIL.ACTIVITIES.EDIT_MODAL_TITLE')
                : t('COMMERCIAL.DETAIL.ACTIVITIES.MODAL_TITLE')
            }}
          </h2>
          <button
            type="button"
            class="rounded-lg p-2 text-n-slate-9 hover:bg-n-alpha-2"
            @click="activityModalOpen = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </header>
        <div class="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-3">
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.TYPE') }}
            <select
              v-model="activityModalForm.activityType"
              required
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            >
              <option
                v-for="type in activityTypes"
                :key="type[0]"
                :value="type[0]"
              >
                {{ type[1] }}
              </option>
            </select>
          </label>
          <label class="text-xs text-n-slate-10 sm:col-span-1 lg:col-span-2">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.SUBJECT') }}
            <input
              v-model.trim="activityModalForm.subject"
              required
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.DATE') }}
            <input
              v-model="activityModalForm.date"
              required
              type="date"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.TIME') }}
            <input
              v-model="activityModalForm.time"
              required
              type="time"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.DURATION') }}
            <input
              v-model.number="activityModalForm.durationMinutes"
              required
              min="1"
              type="number"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.STATUS') }}
            <select
              v-model="activityModalForm.status"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            >
              <option value="pending">
                {{ t('COMMERCIAL.DETAIL.ACTIVITIES.PLANNED_STATUS') }}
              </option>
              <option value="completed">
                {{ t('COMMERCIAL.DETAIL.ACTIVITIES.COMPLETED_STATUS') }}
              </option>
            </select>
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.ASSIGNED_TO') }}
            <select
              v-model="activityModalForm.assignedUserId"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            >
              <option value="">{{ t('COMMERCIAL.UNASSIGNED') }}</option>
              <option
                v-for="agent in store.agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.WHERE') }}
            <input
              v-model.trim="activityModalForm.location"
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.COMPANY') }}
            <input
              v-model="activityModalForm.companyName"
              readonly
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-10"
            />
          </label>
          <label class="text-xs text-n-slate-10">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.CONTACT_NAME') }}
            <input
              v-model="activityModalForm.contactName"
              readonly
              class="mt-1 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-10"
            />
          </label>
          <label class="text-xs text-n-slate-10 sm:col-span-2">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.INVITE_USERS') }}
            <select
              v-model="activityModalForm.invitedUserIds"
              multiple
              class="mt-1 min-h-24 w-full rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
            >
              <option
                v-for="agent in store.agents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </label>
          <label class="text-xs text-n-slate-10 lg:col-span-1">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.INVITE_OTHERS') }}
            <textarea
              v-model.trim="activityModalForm.externalInvitees"
              rows="3"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
              :placeholder="
                t('COMMERCIAL.DETAIL.ACTIVITIES.INVITE_OTHERS_HINT')
              "
            />
          </label>
          <label class="text-xs text-n-slate-10 sm:col-span-2 lg:col-span-3">
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.DESCRIPTION') }}
            <textarea
              v-model.trim="activityModalForm.description"
              rows="4"
              class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-sm"
            />
          </label>
        </div>
        <footer
          class="sticky bottom-0 flex justify-end gap-2 border-t border-n-weak bg-n-background px-5 py-4"
        >
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11"
            @click="activityModalOpen = false"
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.SAVE') }}
          </button>
        </footer>
      </form>
    </div>

    <div
      v-if="cancelActivityCandidate"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      @click.self="cancelActivityCandidate = null"
    >
      <section
        class="w-full max-w-md rounded-2xl border border-n-weak bg-n-background p-5 shadow-2xl"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.DETAIL.ACTIVITIES.CANCEL_DIALOG_TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-10">
          {{ t('COMMERCIAL.DETAIL.ACTIVITIES.CANCEL_DIALOG_DESCRIPTION') }}
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm text-n-slate-11"
            @click="cancelActivityCandidate = null"
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm font-medium text-white hover:bg-n-ruby-10 disabled:opacity-50"
            @click="cancelActivity"
          >
            {{ t('COMMERCIAL.DETAIL.ACTIVITIES.CONFIRM_CANCEL') }}
          </button>
        </div>
      </section>
    </div>

    <div
      v-if="wonOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      @click.self="wonOpen = false"
    >
      <div
        class="w-full max-w-md rounded-xl border border-n-weak bg-n-background p-5 shadow-xl"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.WIN.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-10">
          {{ t('COMMERCIAL.WIN.DESCRIPTION') }}
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="wonOpen = false"
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="rounded-lg bg-n-teal-9 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            @click="markWon"
          >
            {{ t('COMMERCIAL.WIN.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="lossOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="lossOpen = false"
    >
      <form
        class="w-full max-w-md rounded-xl border border-n-weak bg-n-background p-5 shadow-xl"
        @submit.prevent="markLost"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.LOSS.TITLE') }}
        </h2>
        <select
          v-model="lossReasonId"
          required
          class="mt-4 h-9 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2"
        >
          <option value="">{{ t('COMMERCIAL.LOSS.SELECT_REASON') }}</option>
          <option
            v-for="reason in store.lossReasons"
            :key="reason.id"
            :value="reason.id"
          >
            {{ reason.name }}
          </option>
        </select>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg border border-n-weak px-4 py-2 text-sm"
            @click="lossOpen = false"
          >
            {{ t('COMMERCIAL.ACTIONS.CANCEL') }}
          </button>
          <button
            type="submit"
            class="rounded-lg bg-n-ruby-9 px-4 py-2 text-sm text-white"
          >
            {{ t('COMMERCIAL.LOSS.CONFIRM') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
